import { useStore } from 'zustand';
import {
  createAccountStore,
  createAchievementToastOptions,
  createRematchToastOptions,
  toast,
  type TAccountState,
} from '@skillarea/lib-frontend';
import type {
  AccountSSENotificationDto,
  IBaseSingleResultDto,
  NotificationAchievementPayloadDto,
  NotificationRematchPayloadDto,
} from '@skillarea/lib-shared';
import { ENotificationType, formatMoney } from '@skillarea/lib-shared';

import { onboardStore } from './onboardStore';

import i18n from '~/common/i18n';
import { serviceOptions } from '~/services';
import { getConfigs, type IRequestOptions } from '~/services/options';

const rematchExpiryTimers = new Map<string, ReturnType<typeof setTimeout>>();

function clearRematchExpiryTimer(notificationId: string): void {
  const timer = rematchExpiryTimers.get(notificationId);
  if (!timer) return;
  clearTimeout(timer);
  rematchExpiryTimers.delete(notificationId);
}

function getDateMs(value: string | undefined): number | undefined {
  if (!value) return;
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return;
  return ms;
}

function scheduleRematchExpiry(opts: {
  notificationId: string;
  expiresAt?: string;
}): void {
  if (typeof window === 'undefined') return;

  clearRematchExpiryTimer(opts.notificationId);

  const expiresAtMs = getDateMs(opts.expiresAt);
  if (expiresAtMs === undefined) return;

  const delayMs = expiresAtMs - Date.now();
  if (delayMs <= 0) return;

  const timer = setTimeout(() => {
    rematchExpiryTimers.delete(opts.notificationId);
    toast?.dismiss(opts.notificationId);
    void markNotificationRead(opts.notificationId);
  }, delayMs);

  rematchExpiryTimers.set(opts.notificationId, timer);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isAchievementPayload(
  value: unknown,
): value is NotificationAchievementPayloadDto {
  if (!isRecord(value)) return false;
  return (
    typeof value.achievementId === 'number' &&
    typeof value.achievementName === 'string' &&
    typeof value.coins === 'number'
  );
}

function isRematchPayload(
  value: unknown,
): value is NotificationRematchPayloadDto {
  if (!isRecord(value)) return false;
  return (
    typeof value.rematchRequestId === 'string' &&
    typeof value.fromUserId === 'number' &&
    typeof value.fromUsername === 'string' &&
    typeof value.gameId === 'number' &&
    typeof value.gameName === 'string'
  );
}

async function requestApi<T>(
  method: string,
  url: string,
  body: unknown | undefined,
  options: IRequestOptions = {},
): Promise<IBaseSingleResultDto<T>> {
  const configs = getConfigs(method, 'application/json', url, options);
  if (body !== undefined) {
    configs.data = body;
  }

  const requester = serviceOptions.axios?.request;
  if (typeof requester !== 'function') {
    throw new Error('API requester is not configured (serviceOptions.axios)');
  }

  const response = (await requester(configs)) as { data?: unknown };
  return response.data as IBaseSingleResultDto<T>;
}

async function markNotificationRead(notificationId: string): Promise<void> {
  await requestApi(
    'put',
    `/api/notifications/${notificationId}/read`,
    undefined,
  );
}

async function handleRematchAction(opts: {
  action: 'accept' | 'decline';
  notificationId: string;
  rematchRequestId: string;
}) {
  const result = await requestApi(
    'post',
    `/api/matchmaking/rematch/${opts.action}`,
    { rematchRequestId: opts.rematchRequestId },
    {
      errorInterceptor: (response) => {
        if (response.status === 409) return true;
        return undefined;
      },
    },
  );

  if (result.success) {
    clearRematchExpiryTimer(opts.notificationId);
    await markNotificationRead(opts.notificationId);
    toast?.dismiss(opts.notificationId);
    return;
  }

  if (result.status === 409) {
    clearRematchExpiryTimer(opts.notificationId);
    toast?.dismiss(opts.notificationId);
    toast?.warning('Rematch request is no longer available', {
      dedupeKey: `rematch-expired-${opts.notificationId}`,
    });
    await markNotificationRead(opts.notificationId);
    return;
  }
}

export const accountStore = createAccountStore(
  {
    signOut: () => onboardStore.getState().signOutWithConfirm(),
  },
  {
    failedMessage: i18n.t('failedMessage', { ns: 'billing' }),
    successMessageBilling: i18n.t('successMessage', { ns: 'billing' }),
  },
  undefined,
  {
    onNotification: async (notification: AccountSSENotificationDto) => {
      if (notification.type === ENotificationType.achievement) {
        const payload = notification.payload as unknown;

        toast?.success(notification.message, {
          id: notification.notificationId,
          ...(isAchievementPayload(payload)
            ? createAchievementToastOptions(payload, {
                action: {
                  label: 'Achievements',
                  onClick: () => {
                    if (typeof window === 'undefined') return;
                    globalThis.window.location.href = '/account/achievements';
                  },
                },
              })
            : {}),
        });

        await markNotificationRead(notification.notificationId);
        return;
      }

      if (notification.type === ENotificationType.rematch) {
        const payload = notification.payload as unknown;

        const expiresAtMs = getDateMs(notification.expiresAt);
        const isExpired =
          typeof expiresAtMs === 'number' && expiresAtMs <= Date.now();

        if (isExpired) {
          toast?.warning('Rematch request is no longer available', {
            id: notification.notificationId,
          });
          await markNotificationRead(notification.notificationId);
          return;
        }

        if (!isRematchPayload(payload)) {
          toast?.info(notification.message, {
            id: notification.notificationId,
          });

          scheduleRematchExpiry({
            notificationId: notification.notificationId,
            expiresAt: notification.expiresAt,
          });
          return;
        }

        toast?.info(notification.message, {
          id: notification.notificationId,
          ...createRematchToastOptions(
            payload,
            {
              accept: {
                label: 'Accept',
                onClick: () =>
                  void handleRematchAction({
                    action: 'accept',
                    notificationId: notification.notificationId,
                    rematchRequestId: payload.rematchRequestId,
                  }),
              },
              decline: {
                label: 'Decline',
                onClick: () =>
                  void handleRematchAction({
                    action: 'decline',
                    notificationId: notification.notificationId,
                    rematchRequestId: payload.rematchRequestId,
                  }),
              },
            },
            {
              expiresAt: notification.expiresAt,
            },
          ),
        });

        scheduleRematchExpiry({
          notificationId: notification.notificationId,
          expiresAt: notification.expiresAt,
        });
      }
    },
  },
);

accountStore.setState({
  successToast(task) {
    const template = i18n.t('successMessage', { ns: 'billing' });
    const amountText = formatMoney(task.coins, { maxDigits: 2 });
    const message = template
      .replace('{{amount}}', amountText)
      .replace('{amount}', amountText)
      // backward-compat placeholders (older translations/tests)
      .replace('{{coins}}', amountText)
      .replace('{coins}', amountText);

    toast?.success(message);
  },
  errorToast(message) {
    const resolvedMessage =
      message || i18n.t('failedMessage', { ns: 'billing' });

    toast?.error(resolvedMessage);
  },
});

export const useAccountStore = <T>(selector: (s: TAccountState) => T) =>
  useStore(accountStore, selector);

export const useBalance = () => useAccountStore((s) => s.balance);
export const useSubscriptions = () => useAccountStore((s) => s.subscriptions);
